'use client'

import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import { type CrossSellResponse } from '@redteclab/api/clients/retail-media-xsell-service'

import { useGlobalConfigContext } from '../../../global-config'
import { urlResolverGetSearchPage } from '../../../url-handling'
import { type CrossSellWidgetType } from '../../config/CrossSellWidgetType'
import { useCrossSellWidgetTitle } from '../../useCrossSellWidgetTitle'

interface CrossSellWidgetContextValue {
  moreHref?: string
  widgetTitle?: string
}

interface CrossSellWidgetProviderProps {
  data?: CrossSellResponse
  widgetType: CrossSellWidgetType
}

const Context = createContext<CrossSellWidgetContextValue>({})

export const useCrossSellWidgetContext = (): CrossSellWidgetContextValue =>
  useContext(Context)

export const CrossSellWidgetProvider: FC<
  PropsWithChildren<CrossSellWidgetProviderProps>
> = ({ children, data, widgetType }) => {
  const globalConfig = useGlobalConfigContext()
  const widgetTitle = useCrossSellWidgetTitle({ data, widgetType })

  const moreHref = useMemo(
    () =>
      data?.items.length
        ? urlResolverGetSearchPage(globalConfig, {
            queryParams: {
              // eslint-disable-next-line id-length -- search param
              q: data.items.map(({ id }) => id).join(','),
            },
          })
        : undefined,
    [data?.items, globalConfig],
  )

  const value = useMemo(
    () => ({
      moreHref,
      widgetTitle,
    }),
    [widgetTitle, moreHref],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}
